import React, { useEffect, useState, useRef } from 'react';
import { Paper, Typography, Box, Grid, Link } from '@mui/material';
import { Circle } from '@mui/icons-material';
import { Cell, Pie, PieChart } from 'recharts';
import { api } from '../../api/instance';
import Loader from '../../components/Loader/Loader';
import { useAuth } from '../../hooks/useAuth';
import { OpalCard, paperStyles } from '../../components/OpalCard/OpalCard';

const RADIAN = Math.PI / 180;
const COLORS = ['#0088FE', '#00C49F', '#FF8042'];
const truncate = (str, maxLength) => {
    if (str.length > maxLength) {
        return str.slice(0, maxLength - 3) + '...';
    }
    return str;
}

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-(midAngle ?? 0) * RADIAN);
    const y = cy + radius * Math.sin(-(midAngle ?? 0) * RADIAN);

    return (
        <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
            {`${((percent ?? 1) * 100).toFixed(0)}%`}
        </text>
    );
};

const ChannelChart = ({ channels }) => {
    const data = channels.map(item => ({ name: item.channel, value: item.views }));

    return (
        <PieChart width={250} height={200}>
            <Pie
                data={data}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={renderCustomizedLabel}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
            >
                {data.map((entry, index) => (
                    <Cell key={`cell-${entry.name}`} fill={COLORS[index % COLORS.length]} />
                ))}
            </Pie>
        </PieChart>
    )
};

const TopPagesView = ({ pages }) => {
    return (
        <OpalCard>
            <Paper sx={{ ...paperStyles, maxWidth: 800 }}>
                <Typography variant="h5" component="h3" gutterBottom>
                    Top visited pages
                </Typography>
                <Box sx={{ borderBottom: '1px solid #e0e0e0', paddingBottom: 1, marginBottom: 1 }}>
                    <Grid container>
                        <Grid item xs={6}>
                            <Typography variant="subtitle1" fontWeight="bold">Page</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle1" fontWeight="bold" align="right">Visits</Typography>
                        </Grid>
                    </Grid>
                </Box>

                {pages.length > 0 ? (
                    pages.map((page, index) => (
                        <Box key={index} sx={{ borderBottom: '1px dotted #e0e0e0', paddingY: 1 }}>
                            <Grid container alignItems="center">
                                <Grid item xs={6}>
                                    <Typography sx={{ wordWrap: 'break-word' }}>
                                        <Link
                                            target="_blank"
                                            href={`https://mm-h.co.uk${page.page}`}
                                            rel="noreferrer">
                                            {truncate(page.page, 50)}
                                        </Link>
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography align="right">{page.views}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    ))
                ) : (
                    <Typography>No page data available.</Typography>
                )}
            </Paper>
        </OpalCard>
    );
};

const ChannelVisitsView = ({ channels }) => (
    <OpalCard>
        <Paper sx={{ ...paperStyles, maxWidth: 800 }}>
            <Typography variant="h5" component="h3" gutterBottom>
                Visits by channel
            </Typography>
            <Grid container>
                <Grid item xs={7}>
                    <Box sx={{ borderBottom: '1px solid #e0e0e0', paddingBottom: 1, marginBottom: 1 }}>
                        <Grid container>
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight="bold">Page</Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle1" fontWeight="bold" align="right">Visits</Typography>
                            </Grid>
                        </Grid>
                    </Box>
                    {channels.length > 0 ? (
                        channels.map((channel, index) => (
                            <Box key={index} sx={{ borderBottom: '1px dotted #e0e0e0', paddingY: 1 }}>
                                <Grid container alignItems="center">
                                    <Grid item xs={6}>
                                        <Typography>
                                            <Circle style={{ color: channel.channel === "Organic Search" ? '#0088FE' : '#00C49F', fontSize: '0.8rem', marginRight: '0.5rem' }} />
                                            {channel.channel}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography align="right">{channel.views}</Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        ))
                    ) : (
                        <Typography>No page data available.</Typography>
                    )}
                </Grid>
                <Grid item xs={5}>
                    <ChannelChart channels={channels} />
                </Grid>
            </Grid>
        </Paper>
    </OpalCard>
)

const GoogleAnalytics = () => {
    const { isAuthenticated } = useAuth();
    const [pages, setPages] = useState([]);
    const [channels, setChannels] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const retryRef = useRef(0);

    useEffect(() => {
        const getAnalytics = async () => {
            try {
                setLoading(true);
                const response = await api.get('/analytics/page-view');
                if (response.status === 200) {
                    setPages(response.data.pages);
                    setChannels(response.data.channels);
                    setLoading(false);
                } else {
                    setLoading(false);
                    setError(true);
                }
            } catch (error) {
                setError(true);
                retryRef.current = retryRef.current + 1;
            }
        }

        setInterval(() => {
            if (error && retryRef.current < 3) {
                getAnalytics();
            } else {
                return;
            }
        }, 10000);

        isAuthenticated && getAnalytics();
    }, [error, isAuthenticated]);

    return loading || error ? <Loader /> :
        <Box sx={{ flexGrow: 1, marginTop: 3, marginBottom: 3 }}>
            <Typography variant="h4" component="h3">Site visits summary</Typography>
            <Typography variant="body1" gutterBottom>30 DAYS REPORT</Typography>
            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <TopPagesView pages={pages} />
                </Grid>
                <Grid item xs={12} md={6}>
                    <ChannelVisitsView channels={channels} />
                </Grid>
            </Grid>
        </Box>
}

export default GoogleAnalytics